import React, { useState } from 'react';
import { logger } from '../../utils/logger';
import { useAuthStore } from '../../stores/authStore';
import { useFamilyStore } from '../../stores/familyStore';
import { updateUserRole } from '../../utils/accessControl';
import type { FamilyRole } from '../../utils/accessControl';

// Funkcja pomocnicza do normalizacji roli
const normalizeRoleValue = (role: string): FamilyRole => {
  // Usuń białe znaki i zamień na małe litery dla bezpieczniejszego porównania
  const cleanRole = String(role).trim().toLowerCase();
  
  if (cleanRole.includes('parent_a')) return 'parent_a';
  if (cleanRole.includes('parent_b')) return 'parent_b';
  if (cleanRole.includes('child_viewer')) return 'child_viewer';
  
  // Domyślnie zwróć child_viewer
  return 'child_viewer';
};
import { usePermissions } from '../../hooks/usePermissions';

interface MemberRoleManagerProps {
  // memberId: string; // Nieużywane, usuwamy aby uniknąć błędu lintowania
  userId: string;
  currentRole: FamilyRole;
  onRoleUpdated?: () => void;
}

/**
 * Komponent do zarządzania rolą członka rodziny
 */
export const MemberRoleManager: React.FC<MemberRoleManagerProps> = ({
  userId,
  currentRole,
  onRoleUpdated,
}) => {
  const { user } = useAuthStore();
  const { family, fetchFamilyMembers, members } = useFamilyStore();
  const { canManageAccess } = usePermissions();
  // Ustaw początkową wartość roli na podstawie currentRole
  // Upewnij się, że używamy tylko dozwolonych wartości (parent_b lub child_viewer dla nie-administratorów)
  const getInitialRole = (): FamilyRole => {
    // Użyj funkcji normalizującej
    const normalizedRole = normalizeRoleValue(currentRole);
    
    return normalizedRole;
  };
  
  const [role, setRole] = useState<FamilyRole>(getInitialRole());
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Nie pozwól na zmianę własnej roli
  const isSelf = user?.id === userId;
  
  // Nie pozwól na zmianę roli parent_a (Administrator) - jest to rola nieprzenośna
  const isAdminRole = currentRole === 'parent_a';

  const handleRoleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value as FamilyRole;
    logger.debug(`Zmiana roli z ${currentRole} na ${newRole}`);
    setRole(newRole);
    
    // Wyczyść komunikaty przy zmianie roli
    setError(null);
    setSuccess(null);
  };

  const handleUpdateRole = async () => {
    if (!user || !family) return;

    logger.debug(`🚀 Aktualizacja roli z ${currentRole} na ${role}`);
    
    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      // TWARDY WARUNEK: maksymalnie 2 rodziców (parent_a + parent_b)
      if (role === 'parent_b') {
        const parentCount = (members || []).filter(m => {
          const r = normalizeRoleValue(String(m.role))
          return r === 'parent_a' || r === 'parent_b'
        }).length
        const isAlreadyParent = normalizeRoleValue(String(currentRole)) === 'parent_b' || normalizeRoleValue(String(currentRole)) === 'parent_a'
        if (parentCount >= 2 && !isAlreadyParent) {
          setError('Maksymalnie można mieć 2 rodziców w rodzinie (parent_a + parent_b).')
          return
        }
      }

      const updateSuccess = await updateUserRole(userId, family.id, role, user.id);

      if (updateSuccess) {
        logger.debug('✅ Rola zaktualizowana pomyślnie');
        
        // Odśwież listę członków rodziny
        await fetchFamilyMembers(family.id);
        if (onRoleUpdated) {
          onRoleUpdated();
        }
        
        // Wyświetl komunikat sukcesu
        setSuccess('Rola została pomyślnie zaktualizowana!');
        
        // Wyczyść komunikat sukcesu po 3 sekundach
        setTimeout(() => setSuccess(null), 3000);
        
        logger.debug('🎉 Zmiana roli została zapisana!');
      } else {
        setError('Nie udało się zaktualizować roli. Brak uprawnień.');
      }
    } catch (err) {
      setError('Wystąpił błąd podczas aktualizacji roli.');
      console.error('❌ Błąd aktualizacji roli:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  // Funkcja do wyświetlania nazwy roli
  const getRoleDisplayName = (role: FamilyRole): string => {
    // Najpierw znormalizuj rolę
    const normalizedRole = normalizeRoleValue(role);
    
    switch (normalizedRole) {
      case 'parent_a':
        return 'Rodzic / Opiekun (Administrator)';
      case 'parent_b':
        return 'Rodzic / Opiekun';
      case 'child_viewer':
        return 'Obserwator';
      default:
        return String(role);
    }
  };

  // Jeśli użytkownik nie ma uprawnień do zarządzania dostępem, pokaż tylko rolę
  if (!canManageAccess) {
    return (
      <div className="text-sm text-gray-500">
        {getRoleDisplayName(currentRole)}
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-2">
      {isAdminRole ? (
        // Administrator nie może zmienić swojej roli
        <div className="text-sm text-gray-500 font-medium">
          {getRoleDisplayName(currentRole)}
        </div>
      ) : (
        <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rola członka rodziny
              </label>
              <select
                value={role}
                onChange={handleRoleChange}
                disabled={isSelf || isUpdating}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              >
                {/* Tylko parent_a nie może zmienić roli - wszyscy inni mogą być tylko: */}
                {(() => {
                  const parentCount = (members || []).filter(m => {
                    const r = normalizeRoleValue(String(m.role))
                    return r === 'parent_a' || r === 'parent_b'
                  }).length
                  const isAlreadyParent = normalizeRoleValue(String(currentRole)) === 'parent_b' || normalizeRoleValue(String(currentRole)) === 'parent_a'
                  const disableParentOption = parentCount >= 2 && !isAlreadyParent
                  return (
                    <option value="parent_b" disabled={disableParentOption}>
                      {disableParentOption ? 'Rodzic / Opiekun (limit osiągnięty)' : 'Rodzic / Opiekun'}
                    </option>
                  )
                })()}
                <option value="child_viewer">Obserwator</option>
              </select>
            </div>

            {(() => {
              // Użyj globalnej funkcji normalizującej
              const normalizeRole = (roleStr: string): FamilyRole => {
                return normalizeRoleValue(roleStr);
              };
              
              const effectiveCurrentRole = normalizeRole(String(currentRole));
              const effectiveNewRole = normalizeRole(String(role));
              
              // Porównanie znormalizowanych ról
              const hasRoleChanged = effectiveCurrentRole !== effectiveNewRole;
              
              return hasRoleChanged && (
                <div className="flex justify-end pt-3 border-t">
                  <button
                    onClick={handleUpdateRole}
                    disabled={isUpdating}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium flex items-center text-base"
                  >
                    {isUpdating ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Zapisywanie...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">✔️</span>
                        Zapisz zmiany
                      </>
                    )}
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="text-sm text-green-600 mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
            ✅ {success}
          </div>
        )}

        {isSelf && !isAdminRole && (
          <div className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
            ⚠️ Nie możesz zmienić swojej roli.
          </div>
        )}
      </div>
  );
};